import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { TabViewModule } from 'primeng/tabview';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { InputSwitchModule } from 'primeng/inputswitch';
import { DividerModule } from 'primeng/divider';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

type NotificationKey = 'allEnabled' | 'tourAssigned' | 'tourCancelled' | 'availabilityUpdated' | 'blackoutAdded' | 'blackoutRemoved' | 'upcomingTourReminder';

import { SharedService } from 'src/app/core/shared/shared.service';
import { PropertyService } from '../properties/service/property.service';

@Component({
  selector: 'app-tenant-settings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TabViewModule,
    CardModule,
    InputTextModule,
    PasswordModule,
    InputSwitchModule,
    DividerModule,
    ButtonModule,
    ToastModule,
  ],
  templateUrl: './tenant-settings.component.html',
  styleUrl: './tenant-settings.component.scss',
  providers: [MessageService],
})
export class TenantSettingsComponent implements OnInit {

  profileForm!: FormGroup;
  saving = false;
  userId = '';

  passwordForm!: FormGroup;
  savingPassword = false;

  allEnabled = true; 

  notifications: Record<NotificationKey, boolean> = {
    allEnabled:           true,
    tourAssigned:         true,
    tourCancelled:        true,
    availabilityUpdated:  true,
    blackoutAdded:        true,
    blackoutRemoved:      true,
    upcomingTourReminder: false,
  };
  savingNotifications = false;

  notificationItems: { key: NotificationKey; label: string; desc: string }[] = [
    { key: 'tourAssigned',         label: 'Tour Assigned',         desc: 'When admin assigns a tour to you.' },
    { key: 'tourCancelled',        label: 'Tour Cancelled',        desc: 'When an assigned tour gets cancelled.' },
    { key: 'availabilityUpdated',  label: 'Availability Updated',  desc: 'When property admin updates availability.' },
    { key: 'blackoutAdded',        label: 'Blackout Added',        desc: 'When a full-day or time blackout is added.' },
    { key: 'blackoutRemoved',      label: 'Blackout Removed',      desc: 'When a blocked date/time is removed.' },
    { key: 'upcomingTourReminder', label: 'Upcoming Tour Reminder',desc: 'Sent 2 hours before your nearest tour today.' },
  ];

  constructor(
    private fb: FormBuilder,
    private sharedService: SharedService,
    private propertyService: PropertyService,
    private messageService: MessageService,
  ) {}

  static passwordStrength(control: AbstractControl): ValidationErrors | null {
    const v = control.value as string;
    if (!v) return null;
    const errors: ValidationErrors = {};
    if (v.length < 6)          errors['minlength']  = true;
    if (!/[A-Z]/.test(v))      errors['uppercase']  = true;
    if (!/[a-z]/.test(v))      errors['lowercase']  = true;
    if (!/[^A-Za-z0-9]/.test(v)) errors['special']  = true;
    return Object.keys(errors).length ? errors : null;
  }

  static passwordsMatch(control: AbstractControl): ValidationErrors | null {
    const pw  = control.get('newPassword')?.value;
    const cpw = control.get('confirmPassword')?.value;
    return pw && cpw && pw !== cpw ? { mismatch: true } : null;
  }

  ngOnInit(): void {
    this.passwordForm = this.fb.group({
      newPassword:     ['', [Validators.required, TenantSettingsComponent.passwordStrength]],
      confirmPassword: ['', Validators.required],
    }, { validators: TenantSettingsComponent.passwordsMatch });

    const namePattern  = Validators.pattern(/^[a-zA-Z\s\-']+$/);
    const noWhitespace = (c: AbstractControl): ValidationErrors | null =>
      c.value && !c.value.trim() ? { whitespace: true } : null;
    this.profileForm = this.fb.group({
      firstName: ['', [Validators.required, noWhitespace, Validators.maxLength(50), namePattern]],
      lastName:  ['', [Validators.required, noWhitespace, Validators.maxLength(50), namePattern]],
      email:     [{ value: '', disabled: true }],
      phone:     [{ value: '', disabled: true }],
    });

    this.propertyService.getNotificationPreferences().subscribe({
      next: (prefs) => {
        if (prefs) {
          Object.assign(this.notifications, prefs);
          this.allEnabled = this.notifications.allEnabled;
        }
      }
    });
 
    this.sharedService.currentUser$.subscribe((user: any) => {
      if (!user) return;
      this.userId = user._id ?? user.user_id ?? '';
      this.profileForm.patchValue({
        firstName: user.first_name ?? '',
        lastName:  user.last_name  ?? '',
        email:     user.email      ?? '',
        phone:     user.phone      ?? '',
      });
    });

    this.sharedService.loadCurrentUser().subscribe({ error: () => {} });
  }


  saveNotifications(): void {
    if (this.savingNotifications) return;
    this.notifications.allEnabled = this.allEnabled; 
    this.savingNotifications = true;
    this.propertyService.updateNotificationPreferences(this.notifications).subscribe({
      next: () => {
        this.savingNotifications = false;
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Notification preferences saved.' });
      },
      error: () => {
        this.savingNotifications = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save preferences.' });
      },
    });
  }

  savePassword(): void {
    if (this.passwordForm.invalid) return;
    this.savingPassword = true;
    this.propertyService.changePassword(this.passwordForm.value.newPassword).subscribe({
      next: () => {
        this.savingPassword = false;
        this.passwordForm.reset();
        this.messageService.add({ severity: 'success', summary: 'Updated', detail: 'Password changed successfully.' });
      },
      error: (err) => {
        this.savingPassword = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.message || 'Failed to change password.',
        });
      },
    });
  }

  saveProfile(): void {
    if (this.profileForm.invalid || !this.userId) return;
    const first_name = (this.profileForm.value.firstName ?? '').trim();
    const last_name  = (this.profileForm.value.lastName  ?? '').trim();

    this.saving = true;
    this.propertyService.updateUser(this.userId, { first_name, last_name } as any).subscribe({
      next: () => {
        this.saving = false;
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Profile updated successfully.' });
      },
      error: () => {
        this.saving = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update profile.' });
      },
    });
  }
}
